import { Children, useEffect, useState } from 'react';

import { Stepper } from './Stepper';
import { WidgetContext } from './WidgetContext';
import { useWidget } from './useWidget';
import { NavContextProvider } from './NavContext';
import { EventsProvider } from './EventsProvider';
import { pushGAEvent } from '../analytics';
import { getAnalyticsFields, type AnalyticsMap } from '@widget/utils/getAnalyticsFields';

import type { LeadData } from '@widget/core/LeadDataProvider';
import type { ImageAsset } from './types';
import type { StepProps } from './useStepForm';
import type { UseWidgetProps } from './useWidget';
import type { StepperProps } from './Stepper';
import type { ReactElement } from 'react';

export type WidgetProps = Pick<StepperProps, 'render' | 'children'> &
  Omit<UseWidgetProps, 'steps'> & {
    cancelIcon?: ImageAsset | null;
    widgetId: string;
    analyticsMap?: AnalyticsMap;
    leadData: LeadData;
    transactionIdParent?: string;
    showCloseIcon?: boolean;
    maxNumberOfSteps: number;
  };

export const Widget = ({
  children,
  render,
  widgetId,
  on,
  analyticsMap,
  ...widgetProps
}: WidgetProps): JSX.Element => {
  const steps = (Children.toArray(children) as ReactElement<StepProps>[]).map(({ props }) => props);

  const widget = useWidget({
    steps,
    on,
    widgetId,
    analyticsMap,
    ...widgetProps,
  });
  const [hideHeaderText, setHideHeaderText] = useState(false);

  const isStarting = widget.stepIndex === 0;

  useEffect(() => {
    pushGAEvent('step-viewed', {
      ...getAnalyticsFields({ analyticsMap, source: widget.getFormData() }),
      widgetId,
      stepId: widget.activeId,
      prevStepId: widget.prevStepId,
      stepIndex: widget.stepIndex,
      transactionId: widget.transactionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.stepIndex, widget.activeId, widgetId]);

  useEffect(() => {
    if (isStarting) {
      pushGAEvent('widget-started', {
        widgetId,
      });
    }
  }, [widgetId, widget.activeId, isStarting]);

  return (
    <WidgetContext.Provider
      value={{
        steps,
        layoutSettings: { hideHeaderText, setHideHeaderText },
        analyticsMap,
        maxNumberOfSteps: widgetProps.maxNumberOfSteps || 0,
        ...widget,
      }}
    >
      <EventsProvider on={on}>
        <NavContextProvider
          activeId={widget.activeId}
          back={widget.back}
          next={widget.next}
          goTo={widget.goTo}
        >
          <Stepper history={[widget.activeId]} render={render}>
            {children}
          </Stepper>
        </NavContextProvider>
      </EventsProvider>
    </WidgetContext.Provider>
  );
};
